/**
 * Reads the JSON payload TicketTimeEntryTrigger writes into the activity feed.
 *
 * Tolerates a bare number as well, because early rows written before the payload was
 * given a consistent shape stored the minutes on their own.
 */
export const parseTimeEntryActivity = (raw: string | null | undefined) => {
  if (!raw) return null;

  const asNumber = Number(raw);
  if (!Number.isNaN(asNumber) && raw.trim() !== '') {
    return {
      minutes: asNumber,
      originalMinutes: null,
      entryDate: null,
      originalEntryDate: null,
      hasTimeOfDay: null,
      originalHasTimeOfDay: null,
      entryOffsetMinutes: null,
      userId: null
    };
  }

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.minutes !== 'number') return null;
    return {
      minutes: parsed.minutes as number,
      originalMinutes: typeof parsed.originalMinutes === 'number' ? (parsed.originalMinutes as number) : null,

      // The entry's own when, and how to read it. Absent on rows written before the payload
      // carried them, which is why every one of these is nullable rather than defaulted - a
      // missing offset defaulted to zero would silently render an old row in UTC.
      entryDate: typeof parsed.entryDate === 'string' ? (parsed.entryDate as string) : null,
      originalEntryDate:
        typeof parsed.originalEntryDate === 'string' ? (parsed.originalEntryDate as string) : null,
      hasTimeOfDay: typeof parsed.hasTimeOfDay === 'boolean' ? (parsed.hasTimeOfDay as boolean) : null,

      // How to read originalEntryDate, which is not always how to read entryDate: an entry
      // logged before the tenant captured start times gains the flag when it is edited, so on
      // that one save the previous value is a pinned day and the new one is a real instant.
      // Null on an add, and on any row written before this key existed.
      originalHasTimeOfDay:
        typeof parsed.originalHasTimeOfDay === 'boolean' ? (parsed.originalHasTimeOfDay as boolean) : null,
      entryOffsetMinutes:
        typeof parsed.entryOffsetMinutes === 'number' ? (parsed.entryOffsetMinutes as number) : null,

      // Whose time it is, which is not who changed it - an Admin may edit or delete another
      // agent's entry. Null on every row written before this key existed, and the feed reads
      // that as "say nothing", leaving those rows the sentence they have always had rather
      // than guessing an owner from the modifier.
      userId: typeof parsed.userId === 'number' ? (parsed.userId as number) : null
    };
  } catch {
    return null;
  }
};

/**
 * Splits a stored minute total into whole hours and remaining minutes.
 * Minutes are always 0-59; hours are unbounded. Negatives clamp to zero, matching the
 * backend's TimeTrackingFormatter.Split.
 */
export const splitDuration = (totalMinutes: number) => {
  const safe = Math.max(0, Math.floor(totalMinutes || 0));
  return { hours: Math.floor(safe / 60), minutes: safe % 60 };
};

/*
 * Rendering a duration lives in DurationText.tsx, not here. The display patterns carry
 * inline emphasis markup so the numbers can be bold while the joining word is not, which
 * means they have to be rendered as React nodes rather than returned as a string.
 *
 * The selection rules - both parts, hours only, minutes only, with zero components
 * dropped entirely - must stay in step with the backend's TimeTrackingFormatter, which
 * produces the plain-string version for the Teams card.
 */

/**
 * Pulls the unit out of a duration pattern - "<b>{{hours}} Std.</b>" gives "Std." - so the
 * parser below is derived from the very strings used to render, rather than hand-written
 * thirteen times and left to drift.
 */
const unitFrom = (pattern: string, placeholder: string): string =>
  (pattern || '')
    .replace(/<\/?b>/g, '')
    .replace(`{{${placeholder}}}`, '')
    .trim();

/** Escapes a literal so it can sit inside a RegExp - "Std." must not match "Stdx". */
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Turns what an agent typed into the search token TicketTimeEntryTrigger stores, so a
 * duration can be searched in the language it is displayed in.
 *
 * The feed renders through the viewer's own locale - "3h and 4m", "3 Std. und 4 Min.",
 * "3時間4分" - so there is no one stored string every viewer could search. Parsing happens
 * here, where the locale and the patterns already are, and only numbers travel to the
 * server.
 *
 * @param hoursUnit    from time-tracking.duration-hours-only
 * @param minutesUnit  from time-tracking.duration-minutes-only
 * @returns the token to search for, or null when the input is not a duration - a name, say,
 *          which the server resolves separately against the user table.
 */
export const durationSearchToken = (input: string, hoursUnit: string, minutesUnit: string): string | null => {
  const text = (input || '').trim();
  if (!text) return null;

  // Minutes are padded to two digits so a search for 3h3m cannot also match 3h30m:
  // "h3m3" is a substring of "h3m30", "h3m03" is not. Hours are left unpadded and
  // unbounded - "h3m" cannot match "h30m", because the m has to follow the digits.
  const token = (hours: number | null, minutes: number | null) =>
    hours !== null && minutes !== null
      ? `h${hours}m${String(minutes).padStart(2, '0')}`
      : hours !== null
        ? `h${hours}m`
        : `m${String(minutes).padStart(2, '0')}`;

  // A bare number means "this number, in either position" - "3" finds 3h 04m, 3h 30m and
  // 0h 03m. It matches the looser `parts` token rather than `hm`, because no single
  // substring of h3m04 can cover both positions.
  //
  // Deliberately NOT read as a total any more. "184" used to find the 3h 04m entry; nobody
  // searches a duration that way, and having a bare number mean two things is what made "3"
  // find nothing.
  if (/^\d+$/.test(text)) return `|${Number(text)}|`;

  // exec rather than match: neither pattern is global, so both return the same single-match
  // result, and exec is the direct call rather than a dispatch through the string (S6594).
  const h = hoursUnit ? new RegExp(`(\\d+)\\s*${escapeRegExp(hoursUnit)}`, 'i').exec(text) : null;
  const m = minutesUnit ? new RegExp(`(\\d+)\\s*${escapeRegExp(minutesUnit)}`, 'i').exec(text) : null;
  if (!h && !m) return null;

  // No current locale has an hours unit that could match inside its minutes unit - h/min,
  // t/min, Std./Min., 時間/分, ó/p, sa/dk - so the two patterns cannot collide. Worth
  // re-checking if a locale is ever given a unit that is a prefix of the other.
  const hours = h ? Number(h[1]) : null;
  const minutes = m ? Number(m[1]) : null;

  // A displayed duration never shows more than 59 minutes, so "90m" cannot match anything.
  // It used to be converted into a total; with totals gone there is nothing sensible to turn
  // it into, so it falls through to the raw search rather than quietly matching the wrong row.
  if (minutes !== null && minutes > 59) return null;

  return token(hours, minutes);
};

/** The two unit strings for the current locale, read from the same keys used to render. */
export const durationUnits = (t: (key: string) => string) => ({
  hoursUnit: unitFrom(t('time-tracking.duration-hours-only'), 'hours'),
  minutesUnit: unitFrom(t('time-tracking.duration-minutes-only'), 'minutes')
});
